import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import {
  createCoreMetricJob,
  CoreMetricJob,
  CoreMetricJobResult,
  getCoreMetricJob,
  getCoreMetricJobResult,
  getCoreMetricJobs,
} from "../../../api";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import Widgets from "../../../Components/Common/Widgets";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "@/Components/ui";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";


const STATUS_VARIANTS: Record<string, string> = {
  queued: "bg-warning-subtle text-warning",
  running: "bg-info-subtle text-info",
  success: "bg-success-subtle text-success",
  failed: "bg-danger-subtle text-danger",
};


const CoreMetrics = () => {
  document.title = "Core Metrics";

  const [dateRange, setDateRange] = useState<string[]>([
    moment().subtract(6, "days").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ]);
  const [jobs, setJobs] = useState<CoreMetricJob[]>([]);
  const [activeJob, setActiveJob] = useState<CoreMetricJob | null>(null);
  const [result, setResult] = useState<CoreMetricJobResult | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const data = await getCoreMetricJobs();
      setJobs(data);
      if (!activeJob && data.length > 0) {
        setActiveJob(data[0]);
      }
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchResult = async (jobId: number) => {
    setLoadingResult(true);
    try {
      const data = await getCoreMetricJobResult(jobId);
      setResult(data);
    } finally {
      setLoadingResult(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (!activeJob) {
      return;
    }

    if (activeJob.status === "success") {
      fetchResult(activeJob.id);
      return;
    }

    if (activeJob.status === "failed") {
      setResult(null);
      return;
    }

    const intervalId = window.setInterval(async () => {
      const latest = await getCoreMetricJob(activeJob.id);
      setActiveJob(latest);
      setJobs((prevJobs) => prevJobs.map((job) => (job.id === latest.id ? latest : job)));
      if (latest.status === "success") {
        fetchResult(latest.id);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [activeJob?.id, activeJob?.status]);

  const widgetData = useMemo(() => {
    const summary = result?.summary;
    if (!summary) {
      return [
        { label: "DAU", value: "-" },
        { label: "WAU", value: "-" },
        { label: "MAU", value: "-" },
        { label: "방문자", value: "-" },
        { label: "CVR", value: "-" },
      ];
    }

    return [
      { label: "DAU", value: summary.dau_latest.toLocaleString() },
      { label: "WAU", value: summary.wau_latest.toLocaleString() },
      { label: "MAU", value: summary.mau_latest.toLocaleString() },
      { label: "방문자", value: summary.visitors.toLocaleString() },
      { label: "CVR", value: `${summary.cvr.toFixed(2)}%` },
    ];
  }, [result]);

  const chartData = useMemo(() => {
    return (result?.series || []).map((item) => ({
      ...item,
      label: moment(item.date).format("MM/DD"),
    }));
  }, [result]);

  const onCreateJob = async (forceRefresh = false) => {
    if (dateRange.length !== 2) {
      return;
    }

    setSubmitting(true);
    try {
      const job = await createCoreMetricJob({
        start_date: dateRange[0],
        end_date: dateRange[1],
        force_refresh: forceRefresh,
      });
      setActiveJob(job);
      setResult(null);
      await fetchJobs();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid>
      <BreadCrumb title="핵심 지표" pageTitle="통계" />

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <h4 className="card-title mb-1">Active users 기준 핵심 지표</h4>
                  <p className="text-muted mb-0">DAU, WAU, MAU, CVR을 비동기 집계 job으로 조회합니다.</p>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <CommonDatePicker
                    value={dateRange}
                    onChangeDates={(dates) => {
                      if (dates.length === 2) {
                        setDateRange(dates.map((item) => moment(item).format("YYYY-MM-DD")));
                      }
                    }}
                    options={{ mode: "range" }}
                    className="form-control"
                  />
                  <Button color="primary" onClick={() => onCreateJob(false)} disabled={submitting}>
                    {submitting ? <Spinner size="sm" className="me-2" /> : null}
                    집계 실행
                  </Button>
                  <Button color="soft-primary" onClick={() => onCreateJob(true)} disabled={submitting}>
                    강제 재집계
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <Widgets data={widgetData} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={8}>
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">DAU 추이</h4>
            </CardHeader>
            <CardBody style={{ minHeight: 360 }}>
              {loadingResult ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <Spinner />
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="dau" name="DAU" stroke="#0d6efd" strokeWidth={3} dot={false} />
                    <Line type="monotone" dataKey="converters" name="결제 전환자" stroke="#198754" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted">완료된 집계 결과가 없습니다.</div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={4}>
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">선택된 Job</h4>
            </CardHeader>
            <CardBody>
              {activeJob ? (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">상태</span>
                    <Badge className={STATUS_VARIANTS[activeJob.status] || "bg-secondary-subtle text-secondary"}>
                      {activeJob.status}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-muted small">기간</div>
                    <div>{activeJob.start_date} ~ {activeJob.end_date}</div>
                  </div>
                  <div>
                    <div className="text-muted small">요청 시각</div>
                    <div>{moment(activeJob.requested_at).format("YYYY-MM-DD HH:mm:ss")}</div>
                  </div>
                  {activeJob.progress_message ? (
                    <Alert color={activeJob.status === "failed" ? "danger" : "info"} className="mb-0">
                      {activeJob.progress_message}
                    </Alert>
                  ) : null}
                  {activeJob.error_message ? (
                    <Alert color="danger" className="mb-0">
                      {activeJob.error_message}
                    </Alert>
                  ) : null}
                </div>
              ) : (
                <div className="text-muted">선택된 job이 없습니다.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between">
                <h4 className="card-title mb-0">최근 집계 이력</h4>
                {loadingJobs ? <Spinner size="sm" /> : null}
              </div>
            </CardHeader>
            <CardBody>
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>기간</th>
                      <th>상태</th>
                      <th>요청 시각</th>
                      <th>완료 시각</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.length > 0 ? jobs.map((job) => (
                      <tr key={job.id}>
                        <td>{job.id}</td>
                        <td>{job.start_date} ~ {job.end_date}</td>
                        <td>
                          <Badge className={STATUS_VARIANTS[job.status] || "bg-secondary-subtle text-secondary"}>
                            {job.status}
                          </Badge>
                        </td>
                        <td>{moment(job.requested_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                        <td>{job.finished_at ? moment(job.finished_at).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
                        <td className="text-end">
                          <Button color="soft-secondary" size="sm" onClick={() => setActiveJob(job)}>
                            보기
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">집계 이력이 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};


export default CoreMetrics;